import React, { useState, useEffect, useRef } from "react";
import RichTextEditor from "react-rte";
import "react-rte/lib/RichTextEditor.css";

const TextEditor = ({
  initialValue,
  onChange,
  placeholder,
  speechTranscript,
  interimTranscript,
  isRecording,
  hasValidationError = false,
  contentFontFamily,
}) => {
  const [editorValue, setEditorValue] = useState(() => {
    if (initialValue) {
      try {
        return RichTextEditor.createValueFromString(initialValue, "html");
      } catch (error) {
        console.error("Error parsing initial value:", error);
      }
    }
    return RichTextEditor.createEmptyValue();
  });

  // Store the last non-empty interim transcript
  const lastInterimRef = useRef("");

  // Keep editorValue in sync when initialValue prop changes (e.g. when loading a draft)
  useEffect(() => {
    if (initialValue === undefined || initialValue === null) return;

    const currentHtml = editorValue.toString("html");
    if (currentHtml === initialValue) return;

    try {
      const newValue = RichTextEditor.createValueFromString(
        initialValue,
        "html",
      );
      setEditorValue(newValue);
      // Do NOT call onChange here to avoid treating this as a user edit
    } catch (error) {
      console.error("Error updating editor from initialValue:", error);
    }
  }, [initialValue]);

  // Handle changes to the editor
  const handleChange = (value) => {
    setEditorValue(value);
    if (onChange) {
      onChange(value.toString("html"));
    }
  };

  // Handle speech transcripts
  useEffect(() => {
    if (speechTranscript && !isRecording) {
      console.log("Final transcript received:", {
        transcript: speechTranscript,
        isRecording,
        currentContent: editorValue.toString("html"),
      });

      // Get current content without any interim transcript
      const currentContent = editorValue
        .toString("html")
        .replace(/<span class=\"interim-transcript\">.*?<\/span>/, "");

      // Only append if there's actual content to add
      if (speechTranscript.trim()) {
        // Check if current content is empty or just contains <p><br></p>
        const isEmptyContent =
          currentContent === "<p><br></p>" || currentContent === "";
        const newContent = isEmptyContent
          ? speechTranscript
          : currentContent + " " + speechTranscript;

        const newValue = RichTextEditor.createValueFromString(
          newContent,
          "html",
        );
        setEditorValue(newValue);
        if (onChange) {
          onChange(newContent);
        }
      }
      lastInterimRef.current = ""; // Clear interim on final
    }
  }, [speechTranscript, isRecording]);

  // Handle interim transcript
  useEffect(() => {
    if (isRecording) {
      let interimToUse = interimTranscript;
      if (!interimTranscript) {
        // If empty, use the last non-empty interim
        interimToUse = lastInterimRef.current;
      } else {
        // Store the new transcript without appending
        lastInterimRef.current = interimTranscript;
      }
      console.log("Interim transcript received:", {
        transcript: interimToUse,
        isRecording,
        currentContent: editorValue.toString("html"),
      });

      // Get current content without any previous interim transcript
      const currentContent = editorValue
        .toString("html")
        .replace(/<span class=\"interim-transcript\">.*?<\/span>/, "");

      if (interimToUse) {
        const newContent =
          currentContent +
          `<span class=\"interim-transcript\">${interimToUse}</span>`;
        const newValue = RichTextEditor.createValueFromString(
          newContent,
          "html",
        );
        setEditorValue(newValue);
      }
    } else {
      lastInterimRef.current = ""; // Reset on stop
    }
  }, [interimTranscript, isRecording]);

  // Custom toolbar configuration with undo/redo buttons
  const toolbarConfig = {
    display: ["INLINE_STYLE_BUTTONS", "HISTORY_BUTTONS"],
    INLINE_STYLE_BUTTONS: [
      { label: "Bold", style: "BOLD" },
      { label: "Italic", style: "ITALIC" },
      { label: "Underline", style: "UNDERLINE" },
    ],
    HISTORY_BUTTONS: [
      { label: "Undo", style: "UNDO" },
      { label: "Redo", style: "REDO" },
    ],
  };

  const effectiveFontFamily = contentFontFamily || '"Roboto", sans-serif';

  return (
    <div
      className="text-editor-container"
      style={
        hasValidationError
          ? {
              outline: "2px solid var(--color-error)",
              outlineOffset: "-2px",
              borderRadius: "16px",
            }
          : {}
      }
    >
      <RichTextEditor
        value={editorValue}
        onChange={handleChange}
        toolbarConfig={toolbarConfig}
        placeholder={placeholder}
        className="story-writing-area"
      />
      <style jsx>{`
        .text-editor-container {
          position: relative;
          width: 100%;
        }
        .story-writing-area {
          min-height: 500px;
          width: 100%;
          border: 1px solid var(--color-border);
          border-radius: 16px;
          background-color: var(--color-surface);
          color: var(--color-text-tertiary);
          font-family: ${effectiveFontFamily};
        }
        /* Adjust React-RTE toolbar styles */
        :global(.RichTextEditor__root) {
          border-radius: 16px;
          border: 1px solid var(--color-border) !important;
          font-family: "Roboto", sans-serif;
        }
        :global(.public-DraftEditor-content) {
          min-height: 280px;
          padding: 15px;
          font-family: ${effectiveFontFamily};
          font-size: 16px;
          font-weight: 400;
          color: var(--color-text-tertiary);
        }
        /* Normal state */
        :global(.RichTextEditor__toolbar button) {
          color: var(--color-text);
          transition: all 0.2s ease;
        }
        /* Hover state */
        :global(.RichTextEditor__toolbar button:hover) {
          background-color: var(--color-background);
          color: var(--color-primary);
        }
        /* Active state */
        :global(.RichTextEditor__activeButton) {
          background-color: var(--color-primary) !important;
          color: var(--color-surface) !important;
        }
        :global(.RichTextEditor__toolbar) {
          border-bottom: 1px solid var(--color-border) !important;
          border-top-left-radius: 16px;
          border-top-right-radius: 16px;
          background-color: var(--color-surface) !important;
        }
        :global(.RichTextEditor__editor) {
          background-color: var(--color-surface);
          border-bottom-left-radius: 16px;
          border-bottom-right-radius: 16px;
        }
        /* Style for interim transcript */
        :global(.interim-transcript) {
          color: var(--color-text-tertiary);
          font-style: italic;
          opacity: 0.7;
        }
      `}</style>
    </div>
  );
};

export default TextEditor;
